// src/encounter/lib/to-vitals-patient-type.ts
// Resolve the DS VitalsRow `patientType` from the patient's age + sex — the
// context that decides which extra vital rows render (head-circumference for
// infants, LMP for menstruating-age females).
//
// Flutter source of truth: ui_data.dart:1334 `emptyVitalUiList(gender, age)`:
//   - age <= 2            → add Head Circumference (with growth graph)
//   - female & age > 8    → add LMP (last menstrual period)
// The DS VitalsRow gates headCirc on patientType='newborn' and lmp on
// patientType='female', so we collapse age+sex to that single enum. Precedence:
// infant wins (an infant shows head-circ, never LMP), then female>8, else default.
import type { VitalsRowPatientType } from '@scripsteam/scrips-signal-ds';

/** Age (years) at/under which the head-circumference vital is shown (Flutter <= 2). */
export const INFANT_MAX_AGE = 2;
/** Age (years) above which a female patient's LMP vital is shown (Flutter > 8). */
export const LMP_MIN_AGE = 8;

export function resolveVitalsPatientType(
  sex: 'M' | 'F' | 'X' | string | undefined,
  age: number | undefined,
): VitalsRowPatientType {
  // Infant wins regardless of sex — an infant shows head-circ, not LMP.
  if (typeof age === 'number' && age <= INFANT_MAX_AGE) return 'newborn';
  // LMP for females. The >8 gate applies only when age is KNOWN — a known young
  // girl (age 3–8) gets no LMP; a female whose DOB didn't load keeps the LMP slot
  // (preserves the prior sex-only rail behaviour rather than silently hiding it).
  if (sex === 'F' && (age === undefined || age > LMP_MIN_AGE)) return 'female';
  return 'default';
}
